import { AlertConfig } from '../../contacts/user.service';
import { DEFAULT_NOTIFY_WINDOW, DEFAULT_TARGET_TIME } from '../../configuration/configuration.defaults';

export type AlertCycleSlot = {
  date: string;
  time: string;
};

const MINUTES_PER_DAY = 1440;

export function isDayBasedRetryInterval(retryIntervalMinutes: number): boolean {
  return retryIntervalMinutes >= MINUTES_PER_DAY && retryIntervalMinutes % MINUTES_PER_DAY === 0;
}

export function resolveAlertTimeZone(alertConfig: Pick<AlertConfig, 'notifyWindow'>): string {
  return alertConfig.notifyWindow?.timeZone?.trim() || DEFAULT_NOTIFY_WINDOW.timeZone;
}

function resolveTargetTime(alertConfig: Pick<AlertConfig, 'targetTime'>): string {
  return alertConfig.targetTime?.trim() || DEFAULT_TARGET_TIME;
}

function parseHm(hm: string): number {
  const [h, m] = hm.trim().split(':');
  return Number(h) * 60 + Number(m ?? 0);
}

function normalizeHm(hm: string): string {
  const [h, m] = hm.trim().split(':');
  return `${h.padStart(2, '0')}:${(m ?? '00').padStart(2, '0')}`;
}

function addCalendarDaysIso(date: string, days: number): string {
  const [y, mo, d] = date.split('-').map((v) => Number(v));
  const utc = Date.UTC(y, mo - 1, d + days);
  const dt = new Date(utc);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/** Inclusive start, exclusive end — same rule as notify engine. */
export function isInsideNotifyWindow(
  epochMs: number,
  alertConfig: Pick<AlertConfig, 'notifyWindow'>,
): boolean {
  const window = alertConfig.notifyWindow;
  if (!window?.start?.trim() || !window?.end?.trim()) {
    return true;
  }
  const timeZone = resolveAlertTimeZone(alertConfig);
  const startMin = parseHm(window.start);
  const endMin = parseHm(window.end);
  if (endMin <= startMin) {
    return true;
  }
  const slot = epochToSlot(epochMs, timeZone);
  const cur = parseHm(slot.time);
  return cur >= startMin && cur < endMin;
}

/** Inclusive start, exclusive end — same rule as notify engine. */
export function clampEpochToNotifyWindow(
  epochMs: number,
  alertConfig: Pick<AlertConfig, 'notifyWindow'>,
  timeZone: string,
): number {
  const window = alertConfig.notifyWindow;
  if (!window?.start?.trim() || !window?.end?.trim()) {
    return epochMs;
  }
  const startMin = parseHm(window.start);
  const endMin = parseHm(window.end);
  if (endMin <= startMin) {
    return epochMs;
  }

  let ms = epochMs;
  for (let guard = 0; guard < 400; guard++) {
    const slot = epochToSlot(ms, timeZone);
    const cur = parseHm(slot.time);
    if (cur >= startMin && cur < endMin) {
      return slotToEpoch(slot, timeZone);
    }
    if (cur < startMin) {
      return slotToEpoch({ date: slot.date, time: normalizeHm(window.start) }, timeZone);
    }
    const nextDate = addCalendarDaysIso(slot.date, 1);
    ms = slotToEpoch({ date: nextDate, time: normalizeHm(window.start) }, timeZone);
  }
  return epochMs;
}

function clampTargetTimeToWindow(
  time: string,
  alertConfig: Pick<AlertConfig, 'notifyWindow'>,
): string {
  const window = alertConfig.notifyWindow;
  if (!window?.start?.trim() || !window?.end?.trim()) {
    return normalizeHm(time);
  }
  const t = parseHm(time);
  const startMin = parseHm(window.start);
  const endMin = parseHm(window.end);
  if (t >= startMin && t < endMin) {
    return normalizeHm(time);
  }
  return normalizeHm(window.start);
}

/**
 * First alert plus `retryAttempts` follow-ups. Intervals chain from each scheduled slot;
 * every slot is clamped into `notifyWindow`.
 */
export function buildAlertCycle(alertConfig: AlertConfig, startMs: number): AlertCycleSlot[] {
  const timeZone = resolveAlertTimeZone(alertConfig);
  const intervalMinutes = Math.max(1, alertConfig.retryIntervalMinutes);
  const intervalMs = intervalMinutes * 60 * 1000;
  const followUps = Math.max(1, alertConfig.retryAttempts);
  const dayBased = isDayBasedRetryInterval(intervalMinutes);
  const targetTime = clampTargetTimeToWindow(resolveTargetTime(alertConfig), alertConfig);
  const slots: AlertCycleSlot[] = [];

  for (let i = 0; i <= followUps; i++) {
    let slot: AlertCycleSlot;
    if (i === 0) {
      const ms = clampEpochToNotifyWindow(startMs, alertConfig, timeZone);
      const anchor = epochToSlot(ms, timeZone);
      slot = dayBased ? { date: anchor.date, time: targetTime } : anchor;
    } else if (dayBased) {
      const ms = clampEpochToNotifyWindow(startMs + i * intervalMs, alertConfig, timeZone);
      const anchor = epochToSlot(ms, timeZone);
      slot = { date: anchor.date, time: targetTime };
    } else {
      const prevEpoch = slotToEpoch(slots[i - 1], timeZone);
      const ms = clampEpochToNotifyWindow(prevEpoch + intervalMs, alertConfig, timeZone);
      slot = epochToSlot(ms, timeZone);
    }
    slots.push(slot);
  }
  return slots;
}

export function epochToSlot(epochMs: number, timeZone: string): AlertCycleSlot {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(epochMs));

  const year = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const month = parts.find((p) => p.type === 'month')?.value ?? '01';
  const day = parts.find((p) => p.type === 'day')?.value ?? '01';
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';

  return {
    date: `${year}-${month}-${day}`,
    time: `${hour}:${minute}`,
  };
}

export function slotToEpoch(slot: AlertCycleSlot, timeZone: string): number {
  const [year, month, day] = slot.date.split('-').map((v) => Number(v));
  const [hour, minute] = slot.time.split(':').map((v) => Number(v));
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return NaN;
  }

  let utc = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  for (let i = 0; i < 4; i++) {
    const mapped = epochToSlot(utc, timeZone);
    if (mapped.date === slot.date && mapped.time === slot.time) {
      return utc;
    }
    const target = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    const current = Date.UTC(
      Number(mapped.date.slice(0, 4)),
      Number(mapped.date.slice(5, 7)) - 1,
      Number(mapped.date.slice(8, 10)),
      Number(mapped.time.slice(0, 2)),
      Number(mapped.time.slice(3, 5)),
      0,
      0,
    );
    utc += target - current;
  }
  return utc;
}

export const CAMPAIGN_CYCLE_UI_SLOTS = 5;

/** Pad or trim computed cycle to the five wizard rows (empty strings for unused attempts). */
export function cycleSlotsForCampaignUi(
  alertConfig: AlertConfig,
  startMs: number,
  rowCount = CAMPAIGN_CYCLE_UI_SLOTS,
): AlertCycleSlot[] {
  const built = buildAlertCycle(alertConfig, startMs);
  const rows: AlertCycleSlot[] = [];
  for (let i = 0; i < rowCount; i++) {
    rows.push(built[i] ?? { date: '', time: '' });
  }
  return rows;
}
